import React from "react"
import { Eye, Layout, X, Trash2, ArrowUp, ArrowDown } from "lucide-react"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"

import {
  Button,
  Dialog,
  DialogHeader,
  DialogTitle,
  Card,
  CardHeader,
  CardContent,
  Input,
  Label,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@f2e/ui/components/base"
import { DialogContentPlus } from "@f2e/ui/components/base-plus"

import type { RequestFormSchema } from "@/types/requestForm"
import type { FormField, FormFieldType } from "@/types/requestForm"

import SchemaFormView from "./SchemaFormView"
import { DescriptionImageEditor } from "./DescriptionImageEditor"
import {
  allFieldTypes,
  fieldRegistry,
  type FieldRendererProps,
} from "./fieldRegistry"

// Helper functions
const newId = () => Math.random().toString(36).slice(2)

const createField = (type: FormFieldType, t: TFunction): FormField => {
  const question =
    t("workspace.agent.basicForm.formBuilder.defaultQuestion") || "New question"
  return {
    id: newId(),
    type,
    name: `${type}_${newId().slice(0, 4)}`,
    question,
    label: "",
    placeholder: "",
    required: false,
  }
}

const getDisplayNameForType = (type: FormFieldType, t: TFunction): string => {
  return fieldRegistry[type] ? t(fieldRegistry[type].displayNameKey) : type
}

const getFieldRenderer = (type: FormFieldType) => {
  return fieldRegistry[type]?.renderer
}

const getFieldEditor = (type: FormFieldType) => {
  return fieldRegistry[type]?.editor
}

const stopFileOverlay = (e: React.DragEvent) => {
  e.stopPropagation()
  e.preventDefault()
  e.dataTransfer.dropEffect = "move"
  e.dataTransfer.effectAllowed = "move"
}

const CustomFormBuilder: React.FC<{
  schema: RequestFormSchema
  onChange: (next: RequestFormSchema) => void
  disabled?: boolean
  onUploadingChange: (uploading: boolean) => void
}> = ({ schema, onChange, disabled = false, onUploadingChange }) => {
  const { t } = useTranslation()
  const [dragIndex, setDragIndex] = React.useState<number | null>(null)
  const [draggingId, setDraggingId] = React.useState<string | null>(null)
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({})

  const updateSchema = (
    updater: (prev: RequestFormSchema) => RequestFormSchema
  ) => {
    onChange(updater(schema))
  }

  const reorderFields = (from: number, to: number) => {
    if (from === to || from == null || to == null) return
    const nextFields = [...schema.fields]
    const [moved] = nextFields.splice(from, 1)
    nextFields.splice(to, 0, moved)
    updateSchema((prev) => ({ ...prev, fields: nextFields }))
  }

  const updateField = (id: string, updater: (f: FormField) => FormField) => {
    updateSchema((prev) => ({
      ...prev,
      fields: prev.fields.map((f) => (f.id === id ? updater(f) : f)),
    }))
  }

  const deleteField = (id: string) => {
    updateSchema((prev) => ({
      ...prev,
      fields: prev.fields.filter((f) => f.id !== id),
    }))
  }

  const moveField = (id: string, dir: "up" | "down") => {
    const idx = schema.fields.findIndex((f) => f.id === id)
    if (idx < 0) return
    const next = [...schema.fields]
    const newIdx =
      dir === "up" ? Math.max(0, idx - 1) : Math.min(next.length - 1, idx + 1)
    const [item] = next.splice(idx, 1)
    next.splice(newIdx, 0, item)
    updateSchema((prev) => ({ ...prev, fields: next }))
  }

  const clearDragState = () => {
    setDragIndex(null)
    setDraggingId(null)
  }

  return (
    <div
      onDragEnter={stopFileOverlay}
      onDragOver={stopFileOverlay}
      onDragLeave={stopFileOverlay}
      onDrop={stopFileOverlay}
    >
      <CardContent className="space-y-3">
        {schema.fields.length === 0 && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {t("workspace.agent.basicForm.formBuilder.noFieldsYet")}
          </div>
        )}
        {schema.fields.map((field, idx) => {
          const isExpanded = !!expanded[field.id]
          const isDragging = draggingId === field.id
          const SpecRenderer = getFieldRenderer(field.type)
          const EditorComp = getFieldEditor(field.type)

          return (
            <div
              key={field.id}
              className={`group relative bg-white dark:bg-slate-950 border rounded-xl transition-all duration-200 ${
                isExpanded
                  ? "border-indigo-500 ring-1 ring-indigo-500 shadow-md"
                  : "border-slate-200 hover:border-indigo-300 shadow-sm"
              } ${
                isDragging
                  ? "border-dashed border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/20 scale-[1.01] ring-2 ring-indigo-400 shadow-lg"
                  : ""
              }`}
              draggable={!disabled}
              onDragStart={(e) => {
                setDragIndex(idx)
                setDraggingId(field.id)
                e.dataTransfer.dropEffect = "move"
                e.dataTransfer.effectAllowed = "move"
              }}
              onDragOver={(e) => {
                e.preventDefault()
                if (dragIndex === null || dragIndex === idx) return

                // Get the target element's bounding rect and mouse position
                const targetRect = e.currentTarget.getBoundingClientRect()
                const hoverMiddleY = targetRect.top + targetRect.height / 2
                const clientY = e.clientY

                // Determine drag direction and check if we should swap
                // When dragging down (dragIndex < idx): only swap if mouse is below the middle
                // When dragging up (dragIndex > idx): only swap if mouse is above the middle
                const shouldSwap =
                  dragIndex < idx
                    ? clientY > hoverMiddleY
                    : clientY < hoverMiddleY

                if (shouldSwap) {
                  reorderFields(dragIndex, idx)
                  setDragIndex(idx)
                }
              }}
              onDrop={clearDragState}
              onDragEnd={clearDragState}
            >
              {/* Header / Preview Area */}
              <div
                className="p-4 cursor-pointer"
                onClick={() =>
                  !disabled &&
                  setExpanded((prev) => ({
                    ...prev,
                    [field.id]: !prev[field.id],
                  }))
                }
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2 pointer-events-none">
                    {/* Read-only Preview Representation */}
                    {SpecRenderer && (
                      <div className="opacity-60 pointer-events-none">
                        <SpecRenderer
                          field={
                            field as unknown as FieldRendererProps["field"]
                          }
                          mode="preview"
                          contextId="editor-preview"
                        />
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
                      onClick={(e) => {
                        e.stopPropagation()
                        moveField(field.id, "up")
                      }}
                      disabled={disabled || idx === 0}
                      aria-label={t(
                        "workspace.agent.basicForm.formBuilder.moveUp"
                      )}
                    >
                      <ArrowUp className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-800"
                      onClick={(e) => {
                        e.stopPropagation()
                        moveField(field.id, "down")
                      }}
                      disabled={disabled || idx === schema.fields.length - 1}
                      aria-label={t(
                        "workspace.agent.basicForm.formBuilder.moveDown"
                      )}
                    >
                      <ArrowDown className="h-4 w-4" />
                    </Button>
                    <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/30"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteField(field.id)
                      }}
                      disabled={disabled}
                      aria-label={t(
                        "workspace.agent.basicForm.formBuilder.deleteField"
                      )}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Editor Panel (Expanded) */}
              {isExpanded && EditorComp && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-100 dark:border-slate-800 mt-2 bg-slate-50/50 dark:bg-slate-900/30 rounded-b-xl">
                  <EditorComp
                    field={field}
                    onChange={(nf: FormField) =>
                      updateField(field.id, () => nf)
                    }
                    disabled={disabled}
                  />
                  <div className="space-y-1 pt-3">
                    <Label htmlFor={`help-${field.id}`}>
                      {t("workspace.agent.basicForm.formBuilder.description")}
                    </Label>
                    <Textarea
                      id={`help-${field.id}`}
                      value={field.helpText || ""}
                      disabled={disabled}
                      onChange={(event) =>
                        updateField(field.id, (current) => ({
                          ...current,
                          helpText: event.target.value,
                        }))
                      }
                    />
                  </div>
                  <DescriptionImageEditor
                    field={field}
                    onChange={(descriptionImage) =>
                      updateField(field.id, (current) => ({
                        ...current,
                        descriptionImage,
                      }))
                    }
                    imageCount={
                      schema.fields.filter((item) => item.descriptionImage)
                        .length
                    }
                    disabled={disabled}
                    onUploadingChange={onUploadingChange}
                  />
                </div>
              )}
            </div>
          )
        })}
      </CardContent>
    </div>
  )
}

export const FormEditorModal = ({
  schema,
  onChange,
  disabled,
}: {
  schema?: RequestFormSchema
  onChange: (next: RequestFormSchema) => void
  disabled?: boolean
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const [local, setLocal] = React.useState<RequestFormSchema>(() => {
    return (
      schema || {
        id: "runtime",
        title: "",
        description: "",
        fields: [],
      }
    )
  })
  const [isPreviewOpen, setIsPreviewOpen] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)
  const scrollContainerRef = React.useRef<HTMLElement | null>(null)

  const isCopilotTarget = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return false
    return Boolean(
      target.closest("[data-copilot-panel],[data-copilot-launcher]")
    )
  }

  // Sync local state when schema prop changes
  React.useEffect(() => {
    if (schema) {
      setLocal(schema)
    }
  }, [schema])

  // Helper to update local state and notify parent
  const updateLocal = (
    updater: (prev: RequestFormSchema) => RequestFormSchema
  ) => {
    setLocal((prev) => {
      const next = updater(prev)
      onChange(next)
      return next
    })
  }

  const missingAltText = local.fields.some(
    (field) => field.descriptionImage && !field.descriptionImage.altText.trim()
  )
  // Edits already reach the parent, so every close path must wait for valid alt text.
  const closeBlocked = uploading || missingAltText

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || !closeBlocked) setOpen(next)
      }}
      modal={false}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
      >
        {t("workspace.agent.basicForm.unifiedToolSelector.requestFormEditor")}
      </Button>
      <DialogContentPlus
        className="max-w-5xl w-[92vw] h-[82vh] overflow-hidden flex flex-col p-0"
        hideCloseButton={closeBlocked}
        onEscapeKeyDown={(event) => {
          if (closeBlocked) event.preventDefault()
        }}
        onInteractOutside={(event) => {
          if (closeBlocked || isCopilotTarget(event.target)) {
            event.preventDefault()
          }
        }}
      >
        {/* Top Navigation — inspired by formflow-ai */}
        <DialogHeader className="sticky top-0 z-10 bg-white dark:bg-slate-950 border-b px-4 sm:px-6 py-3 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-indigo-600 p-2 rounded-lg text-white shadow-lg shadow-indigo-500/30">
                <Layout size={18} />
              </div>
              <DialogTitle className="text-base sm:text-lg">
                <div className="flex flex-col">
                  <span className="text-xs font-semibold uppercase tracking-wide text-indigo-600">
                    {t("workspace.agent.basicForm.formBuilder.customForm")}
                  </span>
                  <span className="text-sm sm:text-base font-medium text-slate-900 dark:text-slate-50">
                    {local.title?.trim() ||
                      t("workspace.agent.basicForm.formBuilder.untitledForm")}
                  </span>
                </div>
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Button
                type="button"
                variant="ghost"
                className="gap-2 text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
                onClick={() => setIsPreviewOpen(true)}
              >
                <Eye size={16} />
                <span className="hidden sm:inline">
                  {t("workspace.agent.basicForm.formBuilder.previewButton")}
                </span>
              </Button>
              <Button
                type="button"
                className="gap-2 bg-slate-900 text-white hover:bg-slate-800"
                onClick={() => {
                  if (local) onChange(local)
                  setOpen(false)
                }}
                disabled={disabled || closeBlocked}
              >
                <span className="hidden sm:inline">
                  {t("workspace.agent.basicForm.formBuilder.done")}
                </span>
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Main Workspace */}
        <div className="flex flex-1 overflow-hidden bg-slate-50 dark:bg-slate-950/60">
          {/* Left Sidebar - independent column */}
          <aside className="w-72 shrink-0 border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 sm:p-6 overflow-y-auto">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <div className="text-sm font-medium">
                    {t("workspace.agent.basicForm.formBuilder.formSettings")}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-sm">
                      {t("workspace.agent.basicForm.formBuilder.title")}
                    </Label>
                    <Input
                      value={local.title || ""}
                      onChange={(e) => {
                        updateLocal((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }}
                      disabled={disabled}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm">
                      {t("workspace.agent.basicForm.formBuilder.description")}
                    </Label>
                    <Textarea
                      value={local.description || ""}
                      onChange={(e) => {
                        updateLocal((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }}
                      rows={3}
                      disabled={disabled}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="text-sm font-medium">
                    {t("workspace.agent.basicForm.formBuilder.components")}
                  </div>
                </CardHeader>
                <CardContent>
                  <TooltipProvider>
                    <div className="grid grid-cols-1 gap-2">
                      {allFieldTypes.map((typeKey) => {
                        const Icon = fieldRegistry[typeKey].icon
                        const displayName = getDisplayNameForType(typeKey, t)
                        return (
                          <Button
                            key={typeKey}
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              updateLocal((prev) => {
                                const next = {
                                  ...prev,
                                  fields: [
                                    ...prev.fields,
                                    createField(typeKey, t),
                                  ],
                                }
                                // Scroll the right canvas to bottom so the newly added field is visible
                                setTimeout(() => {
                                  const el = scrollContainerRef.current
                                  if (el) {
                                    el.scrollTo({
                                      top: el.scrollHeight,
                                      behavior: "smooth",
                                    })
                                  }
                                }, 0)
                                return next
                              })
                            }}
                            disabled={disabled}
                            className="w-full h-auto min-h-[2rem] px-3 py-2 gap-2 justify-start whitespace-normal text-left"
                          >
                            {Icon && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex">
                                    <Icon className="h-4 w-4 text-muted-foreground" />
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top">
                                  <div className="text-xs">{displayName}</div>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <span className="text-xs leading-snug">
                              {displayName}
                            </span>
                          </Button>
                        )
                      })}
                    </div>
                  </TooltipProvider>
                </CardContent>
              </Card>
            </div>
          </aside>

          {/* Right Canvas - scrollable */}
          <main
            ref={scrollContainerRef}
            className="flex-1 overflow-y-auto p-4 sm:p-8 relative"
          >
            <div className="max-w-3xl mx-auto pb-16">
              <div className="bg-white dark:bg-slate-950 rounded-xl border-t-8 border-t-indigo-500 border-x border-b border-slate-200 dark:border-slate-800 shadow-sm p-4 sm:p-6">
                <CustomFormBuilder
                  schema={local}
                  onChange={(next) => {
                    setLocal(next)
                    onChange(next)
                  }}
                  disabled={disabled || uploading}
                  onUploadingChange={setUploading}
                />
              </div>
            </div>
          </main>
        </div>

        {/* Preview Overlay */}
        {isPreviewOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
              onClick={() => setIsPreviewOpen(false)}
            />
            <div className="relative w-full max-w-2xl bg-white dark:bg-slate-950 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800">
              {/* Gradient top bar */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
              <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-slate-950 z-10">
                <div className="flex items-center gap-2 text-indigo-600">
                  <Eye size={18} />
                  <span className="font-semibold text-sm uppercase tracking-wider">
                    {t("workspace.agent.basicForm.formBuilder.previewButton")}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700 hover:bg-slate-100 dark:text-slate-400 dark:hover:text-white dark:hover:bg-slate-800 rounded-full"
                  onClick={() => setIsPreviewOpen(false)}
                >
                  <X size={18} />
                </Button>
              </div>
              <div className="overflow-y-auto p-6 bg-slate-50 dark:bg-slate-900/40">
                <div className="max-w-xl mx-auto bg-white dark:bg-slate-950 p-6 sm:p-8 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800">
                  <SchemaFormView schema={local} contextId="preview-overlay" />
                  <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 flex items-center justify-end"></div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContentPlus>
    </Dialog>
  )
}

export default FormEditorModal
